import type { RunStatus, SimulationDef } from "../types";

interface RunBarProps {
  readOnly?: boolean;
  definition: SimulationDef;
  definitionName: string | null;
  dirty: boolean;
  ticks: number;
  onTicks: (ticks: number) => void;
  onSeed: (seed: number) => void;
  onName: (name: string) => void;
  activeRun: RunStatus | null;
  canRun: boolean;
  onRun: () => void;
  onCancel: () => void;
  digest: string | null;
  onToast: (text: string) => void;
}

export function RunBar({
  readOnly = false,
  definition,
  definitionName,
  dirty,
  ticks,
  onTicks,
  onSeed,
  onName,
  activeRun,
  canRun,
  onRun,
  onCancel,
  digest,
  onToast,
}: RunBarProps) {
  const running =
    activeRun !== null && (activeRun.status === "queued" || activeRun.status === "running");
  const progress = running && activeRun.ticks ? (activeRun.tick ?? 0) / activeRun.ticks : 0;
  return (
    <header className="run-bar">
      <div className="run-bar-brand">
        SAGA<span>studio</span>
      </div>
      <div className="run-bar-name">
        <input
          className="name-input"
          value={definition.name}
          onChange={(e) => onName(e.target.value)}
          aria-label="definition name"
        />
        {dirty && (
          <span className="dirty-dot" title="unsaved changes">
            ●
          </span>
        )}
        {definitionName && <span className="workspace-tag">{definitionName}</span>}
      </div>
      <label className="run-bar-field">
        seed
        <input
          type="number"
          value={definition.seed}
          onChange={(e) => onSeed(Number(e.target.value))}
        />
      </label>
      {!readOnly && (
        <label className="run-bar-field">
          ticks
          <input
            type="number"
            min={1}
            value={ticks}
            onChange={(e) => onTicks(Math.max(1, Number(e.target.value)))}
          />
        </label>
      )}
      {readOnly ? (
        <span className="run-hint" title="the hosted showcase replays pre-baked runs">
          run it locally — <code>uv run saga dashboard</code>
        </span>
      ) : running ? (
        <div className="run-progress" role="progressbar" aria-valuenow={Math.round(progress * 100)}>
          <div className="run-progress-fill" style={{ width: `${progress * 100}%` }} />
          <span className="run-progress-label">
            {activeRun.tick ?? 0}/{activeRun.ticks}
          </span>
          <button className="btn btn-ghost" onClick={onCancel}>
            cancel
          </button>
        </div>
      ) : (
        <button
          className="btn btn-run"
          disabled={!canRun}
          onClick={onRun}
          title={canRun ? "run this definition" : "fix validation errors first"}
        >
          Run
        </button>
      )}
      {digest && (
        <button
          className="digest-chip"
          title="copy the full digest — every chart traces to a reproducible run"
          onClick={() => {
            void navigator.clipboard.writeText(digest).then(() => onToast("digest copied"));
          }}
        >
          {digest.slice(0, 12)}…
        </button>
      )}
    </header>
  );
}
